import { useState, useEffect, useRef } from 'react';
import moment from 'moment-timezone';
import { Button, Tooltip } from 'primereact';
import { Column } from 'primereact/column';
import { Paginator } from 'primereact/paginator';
import { Checkbox } from 'primereact/checkbox';
import { Skeleton } from 'primereact/skeleton';

import { getTableQuery, prepareQueryWithFilters } from '../helpers/query';
import { FiltersOperators, MODULES } from '../../../../constants';
import { formatDateTime, formatTimeHM } from '../../../../utils/Helpers';
import { TruncatedText } from '../../../shared/PFPrime/TruncatedText';
import {
  bulkUpdateRowsPerPageOptions,
  getEditableKeys,
  groupByRowsPerPageOptions,
  hilightCell,
  INVENTORY_ADJUSTMENT_OBJECT_ID,
  INVENTORY_ADJUSTMENT_PROJECT_OBJECT_ID,
  INVENTORY_ADJUSTMENT_PROJECT_TABLE,
  INVENTORY_ADJUSTMENT_TABLE,
  INVENTORY_ADJUSTMENT_TYPE,
  INVENTORY_ADJUSTMENT_TYPE_PROJECT,
  onCellEditComplete,
} from '../constant/constant';
import { WIDGET_RENDER_FROM } from '../../Dashboard/constants/constants';
import { getDataByCube, getQueryResult } from '../service/widget.service';
import PFFreezeDataTable, {
  buildWidgetFreezeTableKey,
} from '../../../shared/PFPrime/PFFreezeDataTable';
import { encrypt } from '../../../../utils/security.helper';

import BulkUpdateWidgetReport from './BulkUpdateWidgetReport';
import RenderColumnFields from './RenderColumnFields';
import TriggerApiCall from './TriggerApiCall';

const initialInnerPagination = { first: 0, currentPage: 0, perPage: 50 };

const WidgetGroupedTableRenderer = ({
  widgetDetails,
  data: modifiedData,
  tablePagination,
  annotation,
  totalCount,
  rowsPerPageOptions = true,
  orderBy,
  setPaginator,
  paginator,
  filterDetails,
  filters = [],
  homeFilterDateDimension = [],
  tabGrpDep,
  reportTablePrimaryKey,
  isEdit,
  renderFrom,
  refreshDashboardList,
  showAlert,
  freezeTableKey,
  freezeFillSlot = false,
  onGroupExportDataChange,
}) => {
  const [tableDataHeader, setTableDataHeader] = useState([]);
  const groupExportDataRef = useRef([]);
  const [tableHeaderAnnotation, setTableHeaderAnnotation] = useState();
  const [tableAnnotation, setTableAnnotation] = useState();
  const [tableData, setTableData] = useState([]);
  const [expandedRows, setExpandedRows] = useState(null);
  const [expandedRowsCount, setExpandedRowCount] = useState(0);
  const [innerPaginator, setInnerPaginator] = useState(initialInnerPagination);
  const [triggerApi, setTriggerApi] = useState(false);
  const [selectedCells, setSelectedCells] = useState([]);
  const [isLazyLoaded, setIsLazyLoaded] = useState(true);
  const checkRenderFrom = WIDGET_RENDER_FROM.includes(renderFrom);
  const [masterData, setMasterData] = useState({});
  const [loadingChildRecords, setLoadingChildRecords] = useState(false);
  const [isMasterAPICalled, setIsMasterAPICalled] = useState(true);
  if (
    !Array.isArray(widgetDetails?.table_configuration) ||
    !widgetDetails?.table_configuration.length
  )
    return null;

  const headingKeys = [];
  const tableConfiguration = [];
  widgetDetails?.table_configuration.forEach(record => {
    if (!headingKeys.includes(record.measureName)) {
      headingKeys.push(record.measureName);
      tableConfiguration.push(record);
    }
  });
  const formatRowData = (obj, key, localAnnotation) => {
    if (obj[key] == 'Invalid date') {
      obj[key] = '';
    } else if (
      obj?.[key] &&
      (annotation?.[key]?.type == 'time' ||
        tableAnnotation?.[key]?.type == 'time' ||
        localAnnotation?.[key]?.type == 'time')
    ) {
      if (
        annotation?.[key]?.meta?.isDateTimeField ||
        tableAnnotation?.[key]?.meta?.isDateTimeField ||
        localAnnotation?.[key]?.meta?.isDateTimeField
      ) {
        obj[key] = formatDateTime(`${obj[key]}Z`);
      } else if (
        annotation?.[key]?.meta?.isTimeField ||
        tableAnnotation?.[key]?.meta?.isTimeField ||
        localAnnotation?.[key]?.meta?.isTimeField
      ) {
        obj[key] = formatTimeHM(`${obj[key]}Z`);
      } else {
        obj[key] = moment(`${obj[key]}`).format('MM-DD-YYYY');
      }
    } else if (
      annotation?.[key]?.meta?.format ||
      tableAnnotation?.[key]?.meta?.format ||
      localAnnotation?.[key]?.meta?.format
    ) {
      const tempValues = obj[key];
      try {
        const formatter = eval(
          annotation?.[key]?.meta?.format ||
            tableAnnotation?.[key]?.meta?.format ||
            localAnnotation?.[key]?.meta?.format
        );
        obj[key] = formatter(obj[key]);
      } catch (error) {
        console.error('Error : ', error);
        obj[key] = tempValues;
      }
    }

    return obj;
  };

  const refreshTableCount = async headerRowsArr => {
    let measureAnnotation = {};
    const response = await Promise.all(
      headerRowsArr.map(async each => {
        const promises = [];
        const aggrData = {};
        if (
          widgetDetails?.report_group_by_aggr &&
          Object.entries(widgetDetails?.report_group_by_aggr).length
        ) {
          for (const key in widgetDetails?.report_group_by_aggr) {
            if (widgetDetails?.report_group_by_aggr[key]?.measureSummarizedBy) {
              aggrData[
                widgetDetails?.report_group_by_aggr[key].measureSummarizedBy
              ] = 0;
              promises.push(
                fillRowAggregateData(each, [
                  widgetDetails?.report_group_by_aggr[key].measureSummarizedBy,
                ])
              );
            }
          }
        }
        const result = await Promise.all(promises);
        if (Array.isArray(result) && result.length) {
          result.forEach(record => {
            if (record) {
              const { data, annotation } = record;
              if (
                Array.isArray(data) &&
                data.length &&
                data[0] &&
                'object' === typeof data[0]
              ) {
                Object.keys(data[0]).forEach(key => {
                  if (key in aggrData) {
                    aggrData[key] = data[0][key];
                    if (annotation?.measures && annotation?.measures[key])
                      measureAnnotation[key] = annotation.measures[key];
                  }
                });
              }
            }
          });
        }
        return {
          ...each,
          aggrData,
        };
      })
    );
    if (!tableHeaderAnnotation && Object.keys(measureAnnotation).length) {
      setTableHeaderAnnotation(measureAnnotation);
    }
    return response;
  };
  const addFilterInQuery = (query, values) => {
    if (
      Array.isArray(query.filters) &&
      query.filters.length &&
      widgetDetails?.report_group_by?.dimension
    ) {
      const condition = {
        member: widgetDetails?.report_group_by?.dimension,
        values: [values[widgetDetails?.report_group_by?.dimension]?.toString()],
        operator: FiltersOperators[0].key,
      };
      query.filters.push(condition);
      if (Array.isArray(filters) && filters.length)
        query.filters = [...query.filters, ...filters];
    } else if (Array.isArray(filters) && filters.length)
      query.filters = filters;
    if (
      Array.isArray(homeFilterDateDimension) &&
      homeFilterDateDimension.length &&
      Array.isArray(query?.timeDimensions)
    ) {
      homeFilterDateDimension.forEach(timeRecord => {
        query.timeDimensions?.push({
          dimension: timeRecord.dimension,
          dateRange: timeRecord.dateRange,
        });
      });
    }
    return query;
  };
  const fillRowAggregateData = async (values, measures = []) => {
    const formattedQuery = getTableQuery({ ...widgetDetails, measures });
    let query = prepareQueryWithFilters({
      tablePagination,
      query: widgetDetails,
      formattedQuery,
      cubeResponse: [],
      filterDetails,
      orderByConfiguration: orderBy,
      paginator: initialInnerPagination,
    });
    query = addFilterInQuery(query, values);
    const isSupportTicketTable =
      widgetDetails?.custom_properties?.selectedReportTable?.toLocaleLowerCase() ===
      MODULES.SUPPORT.MAIN_CUBE_NAME_LOWERCASE;
    return await getQueryResult(
      query,
      null,
      isSupportTicketTable,
      widgetDetails?.widget_template_id !== undefined &&
        widgetDetails?.widget_template_id !== null &&
        !isNaN(Number(widgetDetails?.widget_template_id))
        ? encrypt(widgetDetails?.widget_template_id)
        : undefined
    );
  };
  const fillRowData = async (values, isExpand = false) => {
    const table_configuration = [...tableConfiguration].filter(
      e => !e?.columnTypeAction
    );
    if (
      !table_configuration.some(
        e => e.measureName === widgetDetails?.report_group_by?.dimension
      )
    ) {
      table_configuration.push({
        alice: values[widgetDetails?.report_group_by?.dimension],
        measureName: widgetDetails?.report_group_by?.dimension,
      });
    }
    const params = {
      ...(widgetDetails || {}),
      table_configuration,
      report_group_by: null,
    };
    const formattedQuery = getTableQuery(params);
    let query = prepareQueryWithFilters({
      tablePagination,
      query: params,
      formattedQuery,
      cubeResponse: [],
      filterDetails,
      orderByConfiguration: orderBy,
      paginator: isExpand ? initialInnerPagination : innerPaginator,
    });
    query.total = true;
    if (
      widgetDetails?.custom_properties?.reportTablePrimaryKey &&
      !query.dimensions.includes(
        widgetDetails.custom_properties.reportTablePrimaryKey
      )
    ) {
      query.dimensions.push(
        widgetDetails.custom_properties.reportTablePrimaryKey
      );
      query.dimensions.push(
        widgetDetails.custom_properties.reportTableSecondaryKey
      );
      if (
        INVENTORY_ADJUSTMENT_PROJECT_TABLE ===
          widgetDetails?.custom_properties?.selectedReportTable &&
        query?.dimensions?.includes(INVENTORY_ADJUSTMENT_TYPE_PROJECT)
      ) {
        query.dimensions.push(INVENTORY_ADJUSTMENT_PROJECT_OBJECT_ID);
      }
      if (
        INVENTORY_ADJUSTMENT_TABLE ===
          widgetDetails?.custom_properties?.selectedReportTable?.replace(
            /\s/g,
            ''
          ) &&
        query?.dimensions?.includes(INVENTORY_ADJUSTMENT_TYPE)
      ) {
        query.dimensions.push(INVENTORY_ADJUSTMENT_OBJECT_ID);
      }
    }
    query = addFilterInQuery(query, values);
    const isSupportTicketTable =
      widgetDetails?.custom_properties?.selectedReportTable?.toLocaleLowerCase() ===
      MODULES.SUPPORT.MAIN_CUBE_NAME_LOWERCASE;
    return await getQueryResult(
      query,
      null,
      isSupportTicketTable,
      widgetDetails?.widget_template_id !== undefined &&
        widgetDetails?.widget_template_id !== null &&
        !isNaN(Number(widgetDetails?.widget_template_id))
        ? encrypt(widgetDetails?.widget_template_id)
        : undefined
    );
  };
  const reArrangeTableData = (param, data, isExpand = false) => {
    let copy = [];
    if (isExpand) {
      copy = [...tableDataHeader];
      const tableIndex = copy.findIndex(
        element =>
          element[widgetDetails?.report_group_by?.dimension] ===
          param[widgetDetails?.report_group_by?.dimension]
      );
      const headerGroupData = copy[tableIndex];
      const addData = data.map((e, index) => ({
        ...e,
        ...headerGroupData,
        uniqueId: index.toString(),
      }));
      // to remove root
      copy.splice(tableIndex, 1);
      // to add at expand index
      copy.splice(tableIndex, 0, addData);
    } else {
      copy = [...tableData];
      // to copy header
      const tableHeader = tableDataHeader.find(
        element =>
          element[widgetDetails?.report_group_by?.dimension] ===
          param[widgetDetails?.report_group_by?.dimension]
      );
      // to add element at last in case of inner pagination
      const tableIndex = copy.findLastIndex(
        element =>
          element[widgetDetails?.report_group_by?.dimension] ===
          param[widgetDetails?.report_group_by?.dimension]
      );
      const addData = data.map(e => ({
        ...e,
        ...tableHeader,
      }));
      // to add at expand index
      copy.splice(tableIndex + 1, 0, addData);
    }
    setTableData(copy.flat());
  };
  const handleExpand = async rowData => {
    setSelectedCells([]);
    sessionStorage.removeItem('viewLinks');
    sessionStorage.removeItem('editLinks');
    let curr = rowData.data;
    if (expandedRows) {
      curr = rowData.data.length
        ? rowData.data.filter(
            element =>
              element[widgetDetails?.report_group_by?.dimension] !==
              expandedRows[0][widgetDetails?.report_group_by?.dimension]
          )
        : [];
    }
    if (!curr.length) {
      setExpandedRows(null);
      setTableData(tableDataHeader);
      return;
    }
    setLoadingChildRecords(true);
    setExpandedRows(curr);
    const response = await fillRowData(curr[0], true);
    setExpandedRowCount(response.total);
    setTableAnnotation(response?.annotation?.dimensions);
    reArrangeTableData(curr[0], response.data, true);
    setLoadingChildRecords(() => false);
  };
  const handleClickMore = async () => {
    setInnerPaginator({
      ...innerPaginator,
      currentPage: innerPaginator.currentPage + 1,
    });
    setTriggerApi(!triggerApi);
  };
  const bodyTemplate = (bodyData, measureName) => {
    if (loadingChildRecords)
      return <Skeleton width="5rem" className="mb-2"></Skeleton>;
    const formatBody = formatRowData({ ...bodyData }, measureName);
    return formatBody[measureName];
  };

  const handleGroupSelection = (groupKey, isChecked) => {
    let currentSelection = Array.isArray(selectedCells)
      ? [...selectedCells]
      : [];

    const groupRows =
      Array.isArray(tableData) &&
      tableData?.filter(
        row => row[widgetDetails?.report_group_by?.dimension] === groupKey
      );
    if (isChecked) {
      groupRows?.forEach(row => {
        if (
          !currentSelection?.some(
            selected =>
              selected[reportTablePrimaryKey] === row[reportTablePrimaryKey] &&
              selected['uniqueId'] === row['uniqueId']
          )
        ) {
          currentSelection?.push(row);
        }
      });
    } else {
      currentSelection = currentSelection?.filter(
        row => row[widgetDetails?.report_group_by?.dimension] !== groupKey
      );
    }

    setSelectedCells(currentSelection);
  };

  const headerTemplate = headerData => {
    const headerDataCopy = formatRowData(
      { ...headerData },
      widgetDetails?.report_group_by?.dimension
    );
    const measures = [];
    if (
      widgetDetails?.report_group_by_aggr &&
      Object.entries(widgetDetails?.report_group_by_aggr).length
    ) {
      for (const key in widgetDetails?.report_group_by_aggr) {
        const headerKey =
          widgetDetails?.report_group_by_aggr[key]?.measureSummarizedBy;
        const measureAnnotation = tableHeaderAnnotation?.[headerKey]
          ? tableHeaderAnnotation
          : {
              [headerKey]: widgetDetails?.report_group_by_aggr[key],
            };
        const measureHeaderCopy = formatRowData(
          { ...headerDataCopy?.aggrData },
          headerKey,
          measureAnnotation
        );
        measures.push({
          name:
            widgetDetails?.report_group_by_aggr[key]?.report_group_aggr_label ||
            widgetDetails?.report_group_by_aggr[key]?.measureName,
          value: measureHeaderCopy?.[headerKey],
        });
      }
    }

    const groupKey = headerData[widgetDetails?.report_group_by?.dimension];
    const groupCheckboxId = `pf-wgg-chk-${String(groupKey ?? '')
      .replace(/[^a-zA-Z0-9_-]/g, '-')
      .slice(0, 96)}`;
    const currentSelection = Array.isArray(selectedCells) ? selectedCells : [];
    const isSelected = tableData
      .filter(
        row => row[widgetDetails?.report_group_by?.dimension] === groupKey
      )
      .every(row => currentSelection?.includes(row));

    return (
      <>
        {isEdit && (
          <>
            <Checkbox
              inputId={groupCheckboxId}
              onChange={e => handleGroupSelection(groupKey, e?.checked)}
              checked={
                Array.isArray(expandedRows) && expandedRows?.length > 0
                  ? isSelected
                  : false
              }
              disabled={
                !(
                  Array.isArray(expandedRows) &&
                  expandedRows?.length > 0 &&
                  expandedRows[0]?.[
                    widgetDetails?.report_group_by?.dimension
                  ] === groupKey
                )
              }
            />
          </>
        )}
        <span className="font-bold w-6 text-base">
          {widgetDetails?.report_group_by?.group_by_label || (
            <span>
              {widgetDetails?.report_group_by?.groupName}{' '}
              {widgetDetails?.report_group_by?.alice}
            </span>
          )}{' '}
          - {headerDataCopy[widgetDetails?.report_group_by?.dimension]}
          {headerDataCopy[
            widgetDetails?.report_group_by?.primaryKeyMeasureName
          ] &&
            `(${headerDataCopy[widgetDetails?.report_group_by?.primaryKeyMeasureName]})`}
        </span>
        <span>
          <div
            className="w-11 flex flex-wrap gap-2"
            style={{ marginLeft: '37px' }}
          >
            {measures.map(({ name, value }, i) => (
              <small key={i}>
                <b>{name}</b> ({value || '-'})
              </small>
            ))}
          </div>
        </span>
      </>
    );
  };
  const footerTemplate = data => {
    return (
      expandedRowsCount -
        (innerPaginator.perPage * innerPaginator.currentPage +
          innerPaginator.perPage) >
        0 && (
        <td colSpan={tableConfiguration.length} className="surface-200 h-10">
          <div className="flex justify-content-center w-full">
            <Button
              label="more..."
              link
              onClick={() => handleClickMore(data)}
            />
          </div>
        </td>
      )
    );
  };
  const columnTemplate = (configuration, isEditable) => {
    return (
      <div className="flex gap-2">
        {isEditable && isEdit && (
          <div className="flex align-items-center gap-2 ">
            <i className="pi pi-pencil text-xs text-color"></i>
          </div>
        )}
        {configuration.alice}
        {widgetDetails?.order_by?.orderField === configuration.measureName &&
          (widgetDetails?.order_by?.orderBy === 'asc' ? (
            <>
              <Tooltip target=".asc" />
              <span>
                <i
                  className="pi pi-sort-amount-up asc"
                  data-pr-tooltip="ascending"
                ></i>
              </span>
            </>
          ) : (
            <>
              <Tooltip target=".desc" />
              <i
                className="pi pi-sort-amount-down desc"
                data-pr-tooltip="decending"
              ></i>
            </>
          ))}
      </div>
    );
  };
  // rearrange Table column
  useEffect(() => {
    if (
      Array.isArray(tableConfiguration) &&
      tableConfiguration.length &&
      Array.isArray(modifiedData) &&
      modifiedData.length
    ) {
      setIsLazyLoaded(true);
      refreshTableCount(modifiedData)
        .then(setTableDataHeader)
        .catch()
        .finally(() => setIsLazyLoaded(false));
    } else setIsLazyLoaded(false);
  }, [modifiedData, tabGrpDep]);
  useEffect(() => {
    if (!Array.isArray(tableDataHeader) || !tableDataHeader.length) {
      groupExportDataRef.current = [];
      onGroupExportDataChange?.([]);
      return;
    }

    let headers = null;
    const rows = tableDataHeader.map(headerData => {
      const headerDataCopy = formatRowData(
        { ...headerData },
        widgetDetails?.report_group_by?.dimension
      );
      const measures = [];
      if (
        widgetDetails?.report_group_by_aggr &&
        Object.entries(widgetDetails?.report_group_by_aggr).length
      ) {
        for (const key in widgetDetails?.report_group_by_aggr) {
          const headerKey =
            widgetDetails?.report_group_by_aggr[key]?.measureSummarizedBy;
          const measureAnnotation = tableHeaderAnnotation?.[headerKey]
            ? tableHeaderAnnotation
            : {
                [headerKey]: widgetDetails?.report_group_by_aggr[key],
              };
          const measureHeaderCopy = formatRowData(
            { ...headerDataCopy?.aggrData },
            headerKey,
            measureAnnotation
          );
          measures.push({
            name:
              widgetDetails?.report_group_by_aggr[key]
                ?.report_group_aggr_label ||
              widgetDetails?.report_group_by_aggr[key]?.measureName,
            value: measureHeaderCopy?.[headerKey],
          });
        }
      }

      const groupColumnLabel =
        widgetDetails?.report_group_by?.group_by_label ||
        `${widgetDetails?.report_group_by?.groupName || ''} ${
          widgetDetails?.report_group_by?.alice || ''
        }`.trim();

      let groupValue =
        headerDataCopy[widgetDetails?.report_group_by?.dimension] ?? '';
      if (
        headerDataCopy[widgetDetails?.report_group_by?.primaryKeyMeasureName]
      ) {
        groupValue = `${groupValue}(${headerDataCopy[widgetDetails?.report_group_by?.primaryKeyMeasureName]})`;
      }

      if (!headers) {
        headers = [groupColumnLabel, ...measures.map(({ name }) => name)];
      }
      return [groupValue, ...measures.map(({ value }) => value ?? '-')];
    });

    groupExportDataRef.current = headers ? [headers, ...rows] : [];
    onGroupExportDataChange?.(groupExportDataRef.current);
  }, [tableDataHeader, tableHeaderAnnotation]);
  // trigger fetch data
  useEffect(() => {
    if (Array.isArray(expandedRows) && expandedRows.length) {
      fillRowData(expandedRows[0])
        .then(({ data }) => {
          reArrangeTableData(expandedRows[0], data);
        })
        .catch();
    }
  }, [triggerApi]);

  const truncatedString = ({ text, maxLength = 20 }) => {
    return <TruncatedText text={text || ''} maxLength={maxLength} />;
  };

  const updateDataArray = (updatedValue, columnReference, itemId) => {
    TriggerApiCall({
      updatedValue,
      columnReference,
      itemId,
      showAlert,
      refreshDashboardList,
    });
  };
  useEffect(() => {
    if (
      tableAnnotation &&
      Object.keys(tableAnnotation)?.length > 0 &&
      isMasterAPICalled
    ) {
      const masterPromissList =
        getEditableKeys(tableAnnotation)?.length > 0 &&
        getEditableKeys(tableAnnotation)?.map(dimensions => {
          const [tableName, columnName] = dimensions.split('.');
          return getDataByCube(tableName, columnName);
        });
      if (masterPromissList.length) {
        Promise.all(masterPromissList).then(response => {
          const masterResponse = {};
          getEditableKeys(tableAnnotation)?.forEach((record, index) => {
            if (response[index]?.length > 0) {
              masterResponse[record] = response[index];
            }
          });
          setMasterData(masterResponse);
        });
      }
      setIsMasterAPICalled(false);
    }
  }, [tableAnnotation]);

  const cellEditor = options => {
    return masterData?.[options?.field] ? (
      <RenderColumnFields
        type="dropdown"
        options={options}
        annotation={tableAnnotation}
        masterData={masterData}
        updateDataArray={updateDataArray}
        reportTablePrimaryKey={reportTablePrimaryKey}
      />
    ) : (
      <RenderColumnFields
        type={tableAnnotation?.[options?.field]?.type}
        options={options}
        annotation={tableAnnotation}
        masterData={masterData}
        updateDataArray={updateDataArray}
        reportTablePrimaryKey={reportTablePrimaryKey}
      />
    );
  };
  return (
    <div
      className={`w-full group-by-widget${freezeFillSlot ? ' flex flex-column flex-1 min-w-0 min-h-0 overflow-hidden' : ''}`}
    >
      {checkRenderFrom && isEdit && (
        <BulkUpdateWidgetReport
          tableConfiguration={tableConfiguration}
          selectedCells={selectedCells}
          masterData={masterData}
          annotation={tableAnnotation}
          reportTablePrimaryKey={reportTablePrimaryKey}
          refreshDashboardList={refreshDashboardList}
          showAlert={showAlert}
          setSelectedCells={setSelectedCells}
        />
      )}
      <PFFreezeDataTable
        responsive
        resizableColumns
        expandableRowGroups
        rowGroupMode="subheader"
        showClear={false}
        scrollable={true}
        loading={isLazyLoaded}
        value={tableData.length ? tableData : tableDataHeader}
        groupRowsBy={widgetDetails?.report_group_by?.dimension}
        expandedRows={expandedRows}
        onRowToggle={handleExpand}
        rowGroupHeaderTemplate={headerTemplate}
        rowGroupFooterTemplate={footerTemplate}
        tableStyle={{ minWidth: '50rem' }}
        tableClassName="w-12"
        selection={selectedCells}
        onSelectionChange={e => {
          setSelectedCells(e.value);
        }}
        selectionMode={isEdit ? 'checkbox' : false}
        metaKeySelection={true}
        editMode={isEdit ? 'cell' : null}
        className="pf-data-table p-datatable-striped"
        showFreeze={true}
        tableKey={
          freezeTableKey ?? buildWidgetFreezeTableKey(widgetDetails, 'grouped')
        }
        freezeFillSlot={freezeFillSlot}
      >
        {isEdit ? (
          <Column
            selectionMode={isEdit ? 'multiple' : false}
            headerStyle={{ width: '3rem' }}
          />
        ) : null}
        {tableConfiguration.map((configuration, index) => (
          <Column
            columnTypeAction={Boolean(configuration?.columnTypeAction)}
            freezeLabel={configuration.alice}
            bodyClassName={hilightCell(
              tableAnnotation?.[configuration?.measureName]?.meta
                ?.isEditable !== undefined &&
                tableAnnotation[configuration?.measureName]?.meta?.isEditable,
              isEdit
            )}
            key={index}
            showAddButton={false}
            showFilterOperator={false}
            style={{ width: '20%' }}
            field={configuration.measureName}
            header={columnTemplate(
              configuration,
              tableAnnotation?.[configuration?.measureName]?.meta
                ?.isEditable !== undefined &&
                tableAnnotation?.[configuration?.measureName]?.meta?.isEditable,
              orderBy
            )}
            body={
              configuration?.columnTypeAction
                ? (params, index) =>
                    loadingChildRecords ? (
                      <Skeleton width="5rem" className="mb-2"></Skeleton>
                    ) : (
                      configuration.options.customBodyRenderLite(params, index)
                    )
                : annotation?.[configuration.measureName]?.meta?.isUrl
                  ? (params, index) => (
                      <a href={params?.[index?.field]}>
                        {params?.[index?.field]}
                      </a>
                    )
                  : configuration?.measureName &&
                      tableAnnotation?.[configuration.measureName]?.meta
                        ?.isTruncate
                    ? params =>
                        truncatedString({
                          text: params[configuration.measureName],
                          maxLength:
                            tableAnnotation?.[configuration.measureName]?.meta
                              ?.isTruncate?.maxLength,
                        })
                    : bodyData =>
                        bodyTemplate(bodyData, configuration?.measureName)
            }
            {...(configuration?.measureName?.length > 0
              ? {
                  editor: options => cellEditor(options),
                }
              : {})}
            onCellEditComplete={onCellEditComplete}
          />
        ))}
      </PFFreezeDataTable>
      {(tableData.length > 0 || tableDataHeader.length > 0) && (
        <Paginator
          className={freezeFillSlot ? 'flex-shrink-0' : undefined}
          first={paginator.first}
          rows={paginator.perPage}
          totalRecords={totalCount}
          onPageChange={event => {
            setIsLazyLoaded(true);
            setTableData([]);
            setExpandedRows(null);
            setPaginator({
              ...paginator,
              currentPage: event.page,
              perPage: event.rows,
              first: event.first,
            });
          }}
          rowsPerPageOptions={
            rowsPerPageOptions &&
            (isEdit ? bulkUpdateRowsPerPageOptions : groupByRowsPerPageOptions)
          }
          template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
          currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
        />
      )}
    </div>
  );
};
export default WidgetGroupedTableRenderer;
